/**
 * Settle, archive, and restore thread lifecycle actions with their undo
 * wiring (ticket 3531, upstream pin e3e7cc3f, MIT). Extracted from
 * `useThreadActions.ts` so that file stays within its size ratchet. The undo
 * claim flow lives in `hooks/threadUndo`: reverse actions invalidate any
 * pending claim first, forward actions publish an undo notice on success.
 */

import { scopedThreadKey } from "@cmmd-forge/client-runtime";
import { type ScopedThreadRef } from "@cmmd-forge/contracts";
import { useCallback } from "react";

import { resolveThreadLifecycleDispatch } from "../lib/resolveThreadLifecycleDispatch";
import { readEnvironmentApi } from "../environmentApi";
import { showThreadUndoNotice } from "./showThreadUndoNotice";
import * as ThreadUndo from "./threadUndo";
import { archiveThreadWithWorktreeRelease } from "./archiveThreadWithWorktreeRelease";
import {
  canArchiveThread,
  dispatchUnarchiveCommand,
  resolveArchiveAction,
  resolveArchiveDetails,
  resolveThreadOrNull,
  resolveThreadOrThrow,
  resolveUnarchiveDispatch,
  type ResolvedThreadTarget,
} from "./useThreadActions.archive";
import type { SidebarThreadSummary } from "../types";

type ResolvedArchive = ReturnType<typeof resolveArchiveDetails>["archive"];

/** Build the CMMD archive release request from the thread's archive record. */
function archiveReleasePayload(
  target: ScopedThreadRef,
  thread: Pick<SidebarThreadSummary, "projectId">,
  archive: ResolvedArchive,
  archiveState: string | null,
  force: boolean,
) {
  const operationId = archive?.operationId;
  return {
    threadRef: target,
    projectId: thread.projectId,
    action: resolveArchiveAction(archiveState),
    force,
    expectedRevision: archive?.revision ?? 0,
    ...(operationId !== null && operationId !== undefined ? { operationId } : {}),
  };
}

/**
 * The four lifecycle callbacks. `resolveThreadTarget` is the host hook's
 * store-reading resolver, shared so every action sees the same resolution.
 */
export function useThreadLifecycleActions(
  resolveThreadTarget: (target: ScopedThreadRef) => ResolvedThreadTarget | null,
) {
  const unarchiveThread = useCallback(
    async (
      target: ScopedThreadRef,
      opts: {
        /** The Activity sidebar can render CMMD replicas that are not in the live store. */
        readonly threadSnapshot?: SidebarThreadSummary;
        readonly force?: boolean;
      } = {},
    ) => {
      // A manual restore supersedes any pending undo for this thread.
      ThreadUndo.invalidate("archive", scopedThreadKey(target));
      const resolved = resolveThreadTarget(target);
      const thread = resolveThreadOrThrow(
        target,
        resolved,
        opts.threadSnapshot,
        "Thread snapshot does not match the archive target.",
        "Thread is unavailable for archive restoration.",
      );
      const { archive } = resolveArchiveDetails(thread, opts.threadSnapshot);
      const archiveState = archive?.state ?? null;
      if (archiveState === "applying") {
        throw new Error("Archive cleanup is already applying and cannot be cancelled.");
      }
      const { action, runtimeApi } = resolveUnarchiveDispatch(archiveState, () =>
        readEnvironmentApi(target.environmentId),
      );
      await archiveThreadWithWorktreeRelease({
        threadRef: target,
        projectId: thread.projectId,
        action,
        expectedRevision: archive?.revision ?? 0,
        ...(archive?.operationId ? { operationId: archive.operationId } : {}),
      });
      await dispatchUnarchiveCommand(runtimeApi, target.threadId);
    },
    [resolveThreadTarget],
  );

  const unsettleThread = useCallback(
    async (
      target: ScopedThreadRef,
      opts: {
        /** The Activity sidebar can render CMMD replicas that are not in the live store. */
        readonly threadSnapshot?: SidebarThreadSummary;
      } = {},
    ) => {
      // A manual restore supersedes any pending undo for this thread.
      ThreadUndo.invalidate("settle", scopedThreadKey(target));
      const resolved = resolveThreadTarget(target);
      const thread = resolveThreadOrNull(
        target,
        resolved,
        opts.threadSnapshot,
        "Thread snapshot does not match the restore target.",
      );
      if (!thread) return;
      await resolveThreadLifecycleDispatch(target, thread.projectId, "restore");
    },
    [resolveThreadTarget],
  );

  const archiveThread = useCallback(
    async (
      target: ScopedThreadRef,
      opts: {
        /** The Activity sidebar can render CMMD replicas that are not in the live store. */
        readonly threadSnapshot?: SidebarThreadSummary;
        readonly force?: boolean;
      } = {},
    ) => {
      const resolved = resolveThreadTarget(target);
      const thread = resolveThreadOrThrow(
        target,
        resolved,
        opts.threadSnapshot,
        "Thread snapshot does not match the archive target.",
        "Thread is unavailable for archiving.",
      );
      const { archive, archivedAt } = resolveArchiveDetails(thread, opts.threadSnapshot);
      const archiveState = archive?.state ?? null;
      if (!canArchiveThread(thread, archivedAt, archiveState)) return false;
      const claim = ThreadUndo.begin("archive", scopedThreadKey(target));
      try {
        await archiveThreadWithWorktreeRelease(
          archiveReleasePayload(target, thread, archive, archiveState, opts.force ?? false),
        );
      } catch (error) {
        claim.finish();
        throw error;
      }
      showThreadUndoNotice({
        action: "Archived",
        claim,
        failureTitle: "Undo archive failed",
        undo: () => unarchiveThread(target),
      });
      return true;
    },
    [resolveThreadTarget, unarchiveThread],
  );

  const settleThread = useCallback(
    async (
      target: ScopedThreadRef,
      opts: {
        /** The Activity sidebar can render CMMD replicas that are not in the live store. */
        readonly threadSnapshot?: SidebarThreadSummary;
      } = {},
    ) => {
      const resolved = resolveThreadTarget(target);
      const thread = resolveThreadOrNull(
        target,
        resolved,
        opts.threadSnapshot,
        "Thread snapshot does not match the settle target.",
      );
      if (!thread) return;
      const claim = ThreadUndo.begin("settle", scopedThreadKey(target));
      try {
        await resolveThreadLifecycleDispatch(target, thread.projectId, "settle");
      } catch (error) {
        claim.finish();
        throw error;
      }
      showThreadUndoNotice({
        action: "Settled",
        claim,
        failureTitle: "Undo settle failed",
        undo: () => unsettleThread(target),
      });
    },
    [resolveThreadTarget, unsettleThread],
  );

  return { unarchiveThread, unsettleThread, archiveThread, settleThread };
}
